<template>
  <Loading v-if="loading" />
  <div v-else-if="error">
    <PageNotFound title="" :button="`${Globals.APP_NAME}`" subtitle2="" :subtitle1="error" />
  </div>
  <div v-else>

    <div class="flex flex-col lg:flex-row min-h-screen container mx-auto">
      <div class="flex-1 p-6 sm:p-8 md:p-12 lg:w-3/5 lg:py-16 lg:px-24 xl:py-16">
        <component :is="currentStepComponent" v-bind="currentStepProps" />
      </div>
      <div v-if="!lastStep" class="mt-12 hidden lg:block lg:w-2/5 bg-neutral-50 p-6 xl:p-8 lg:py-8">
        <div class="sticky top-4">
          <Summary v-if="order.selectedGroup" :order="order" />
          <!-- payment method -->
  <img v-if="currentStepName==='ConfirmationSummary'" src="../assets/images/payment-methods.png" alt="Metody płatności" class="mt-12 h-8 object-contain" />
        </div>
      </div>
    </div>



  </div>
</template>

<script setup>
import rpAPI from '@shared/service/rpAPI.js';
import settings from '@shared/model/Settings.js';
import Order from '@shared/model/Order.js';
import Globals from '@shared/model/Globals.js';

import { useRoute } from 'vue-router';
import { ref, computed, watch, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';


import GroupSelector from '@/components/GroupSelector.vue';
import Register from '@/components/Register.vue';
import PriceAddons from '@/components/PriceAddons.vue';
import InsuranceAddons from '@/components/InsuranceAddons.vue';
import AuthUser from '@/components/AuthUser.vue';
import PageNotFound from '@/pages/PageNotFound.vue';
import ConfirmationSummary from '@/components/ConfirmationSummary.vue'
import Loading from '@/components/Loading.vue';
import Summary from '@/components/Summary.vue';
import ReservationModel from '@shared/model/Reservation.js';
import CheckoutPayment from '@/components/CheckoutPayment.vue'; 

const version = __APP_VERSION__;

// Define props
const route = useRoute();
const id = ref(route?.params.id);
const groupId = ref(route?.params.groupId ?? null);
const loading = ref(true);
const error = ref(null);
const order = ref({});
const steps = ref([]);
const currentStep = ref(0);

const { t } = useI18n();

function buildSteps() {
  const result = [];
  // Always start with term selection
  let reservation = order.value?.reservation;
  let selectedGroup = order.value?.selectedGroup || null;


  if (!selectedGroup) {
    selectedGroup = reservation.groups[0].id;
  }


  if (!reservation || !reservation.groups || reservation.groups.length === 0) {
    return result; // Return empty if no groups
  }

  if (reservation.groups) {
    result.push({
      name: 'SelectGroup',
      component: markRaw(GroupSelector),
      props: () => ({
        reservation: reservation,
        groups: reservation.groups,
        order: order.value,
        'onUpdate:selectedGroup': () => {
          console.log('Selected group updated:', order.value.selectedGroup);
          steps.value = buildSteps();
          const groupStepIndex = steps.value.findIndex(step => step.name === 'SelectGroup');
          currentStep.value = groupStepIndex;
        },
        onNext: nextStep,
        onCancel: goToReferrer,
        onPrev: goToReferrer
      })
    });
  }



  // if (selectedGroup && selectedGroup?.location?.discount_NW == true || selectedGroup?.location?.discount_NWMAX == true ) {
  //   result.push({
  //     name: 'BonusCards',
  //     component: markRaw(BonusCardSelector),
  //     props: () => ({ order: order.value, onCancel: previousStep, onNext: nextStep })
  //   });
  // }
  // Add-ons step if available
  if (reservation.addOns) {
    result.push({
      name: 'AddOns',
      component: markRaw(PriceAddons),
      props: () => ({ order: order.value, onNext: nextStep, onPrev: previousStep })
    });
  }
  // Insurance step if available
  if (reservation.insurance) {
    result.push({
      name: 'Insurance',
      component: markRaw(InsuranceAddons),
      props: () => ({ settings: settings, order: order.value, onCancel: previousStep, onNext: nextStep })
    });
  }
  // Always include these steps
  result.push({
    name: 'Authorize',
    component: markRaw(AuthUser),
    props: () => ({ order: order.value, onCancel: previousStep, onNext: nextStep })
  });
  result.push({
  name: 'Register',
    component: markRaw(Register),
    props: () => ({ settings: settings, order: order.value, onCancel: previousStep, onNext: nextStep })
  });

  // RLO, AKA nie uzywa particpiants, to trzeba konfigurowac na poziomie 
  // Kursu. Np. czy Kurs jest dla osoby indywidualnej czy dla dziecka, grupy
  // wøwczas nie ma sensu pokazywać tego kroku.

  // result.push({
  //   name: 'Participants',
  //   component: markRaw(Participiants),
  //   props: () => ({ settings: settings, order: order.value, onCancel: previousStep, onNext: nextStep })
  // });

  // Agreements step together on confirmation 
  // This should be configurable on the settings level
  // if (selectedGroup && selectedGroup?.agreements?.length > 0) {
  //   result.push({
  //     name: 'Agreements',
  //     component: markRaw(AgreementSelector),
  //     props: () => ({ order: order.value, onCancel: previousStep, onNext: nextStep })
  //   });
  // }

  result.push({
    name: 'ConfirmationSummary',
    component: markRaw(ConfirmationSummary),
    props: () => ({ order: order.value, onCancel: previousStep, onNext: nextStep })
  });


  result.push({
    name: 'CheckoutPayment',
    component: markRaw(CheckoutPayment),
    props: () => ({
      order: order.value,
      paymentLink: order.value.paymentLink ?? order.value.sendOrder?.details?.payment?.payment_link ?? null,
      onCancel: previousStep,
      onNext: nextStep
    })
  });
  

  return result;
}

const fetchReservation = async (id, isTrial = false) => {
  loading.value = true;
  error.value = null;
  try {
    const fetched = await ReservationModel.fetchBySlug(id);
    if (!fetched) {
      throw new Error("Oferta jest już nieaktualna");
    }
    order.value = new Order(fetched);
    // Ensure these fields exist early (so later assignments in steps/components won't be "new" props).
    order.value.sendOrder = order.value.sendOrder ?? null;
    order.value.paymentLink = order.value.paymentLink ?? null;

    let group = order.value.reservation.groups[0] || null;
    if (groupId.value) {
      group = order.value.reservation.groups.find(g => g.id === parseInt(groupId.value, 10));
      if (group) {
        order.value.setGroup(group);
        // remove all other groups
        order.value.reservation.groups = [group];
        // RLO, AKA włącz domyślnie 1 osobę
       
      }
    }
    if (!group) {
      throw new Error("Aktualnie nie ma dostępnych grup");
    }

    const savedOrder = Order.getSavedOrder();
    if (savedOrder && rpAPI.isLoggedIn()) {
      if (savedOrder.reservation.id === order.value.reservation.id && savedOrder.selectedGroup?.id === group.id) {
        order.value.copyOrder(savedOrder);
      } 
    }
    // RLO, if user is not logged in, clear order
    if (!rpAPI.isLoggedIn()) {
        order.value.clearOrder();
    } else {
        order.value.setCustomer(rpAPI.user);
    }

    order.value.setGroup(group);
    if (!group?.noAvailability) {
      order.value.setCount('all', 1);
    }

    if (isTrial && group?.hasTrial) {
      order.value.onTrial = true;
    }

    if (!settings?.offDays?.[group.year]) {
      await settings.fetchOffDays(group.year);
    }


    currentStep.value = order.value.currentStep; 

    steps.value = buildSteps();

     if (currentStep.value >= 0 && currentStep.value < steps.value.length) {
      // valid step, do nothing
    } else {
      currentStep.value = 0;
      order.value.currentStep = 0;
      order.value.saveOrder();
    }

    // RLO, to mozna lepiej obsłuzyć pamiętając w order czy zostało wysłane. 
    if (lastStep.value) {
      order.value.clearOrder();
      currentStep.value = 0;
      steps.value = buildSteps();
    }

  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};



watch(() => route?.params?.id, (newId) => {
  // Check for ?trial param in route.query
  const isTrial = route?.query?.trial !== undefined;
  if (newId) {
    id.value = newId;
    fetchReservation(id.value, isTrial);
  } else {
    error.value = "Nie znaleziono oferty.";
    loading.value = false;
  }
}, { immediate: true });




const currentStepComponent = computed(() => steps.value[currentStep.value]?.component || null);
const currentStepProps = computed(() => {
  const step = steps.value[currentStep.value];
  if (!step) return {};
  // If props is a function, call it; if it's an object, return as is; if undefined, return empty object
  if (typeof step.props === 'function') return step.props();
  if (typeof step.props === 'object') return step.props;
  return {};
});

const lastStep = computed(() => {
  return steps.value[currentStep.value]?.name === 'CheckoutPayment';
});

const currentStepName = computed(() => {
  return steps.value[currentStep.value]?.name || null;
});


function resetScrollPosition() {
  if (window.innerWidth <= 768) { // Check if the device is mobile
    window.scrollTo(0, 0);
  }
}

function nextStep() {
  if (currentStep.value < steps.value.length - 1) currentStep.value++;
  order.value.currentStep = currentStep.value;
  order.value.saveOrder();
  resetScrollPosition(); 

}
function previousStep() {
  if (currentStep.value > 0) currentStep.value--;
  order.value.currentStep = currentStep.value;
  order.value.saveOrder();
  resetScrollPosition();
}

function goToReferrer() {
  const referrer = document.referrer;
  const homeUrl = Globals.APP_HOME_URL;
  if (referrer && referrer !== window.location.href) {
    window.location.href = referrer;
  } else {
    window.location.href = homeUrl;
  }
}


</script>
